import { useState, useEffect } from 'react';
import { useAppData } from '../context/AppDataContext';

/**
 * Custom hook for managing device selection
 * Provides functions to get, set, and manage selected devices
 */
export const useDeviceSelection = () => {
  const { 
    getDeviceList, 
    getSelectedDevices, 
    saveSelectedDevices, 
    getSelectedDeviceObjects 
  } = useAppData();
  
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load selected devices on mount
  useEffect(() => {
    try {
      const storedDevices = getSelectedDevices();
      setSelectedDevices(storedDevices);
    } catch (error) {
      console.error('Error loading selected devices:', error);
      setError('Failed to load device preferences');
    }
  }, [getSelectedDevices]);

  // Get available devices
  const availableDevices = getDeviceList();

  // Update selected devices
  const updateSelectedDevices = (deviceIds) => {
    setSelectedDevices(deviceIds);
    setError(null);
  };

  // Add device to selection
  const addDevice = (deviceId) => {
    if (!selectedDevices.includes(deviceId)) {
      const newSelection = [...selectedDevices, deviceId];
      updateSelectedDevices(newSelection);
    }
  };

  // Remove device from selection
  const removeDevice = (deviceId) => {
    const newSelection = selectedDevices.filter(id => id !== deviceId);
    updateSelectedDevices(newSelection);
  };

  // Toggle device selection
  const toggleDevice = (deviceId) => {
    if (selectedDevices.includes(deviceId)) {
      removeDevice(deviceId);
    } else {
      addDevice(deviceId);
    }
  };

  // Select all devices
  const selectAllDevices = () => {
    const allDeviceIds = availableDevices.map(device => device.id);
    updateSelectedDevices(allDeviceIds);
  };

  // Deselect all devices
  const deselectAllDevices = () => {
    updateSelectedDevices([]);
  };

  // Save selected devices to localStorage
  const saveDevices = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const success = saveSelectedDevices(selectedDevices);
      if (!success) {
        throw new Error('Failed to save devices');
      }
      return true;
    } catch (error) {
      console.error('Error saving devices:', error);
      setError('Failed to save device preferences');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Get selected device objects
  const selectedDeviceObjects = getSelectedDeviceObjects();

  // Check if device is selected
  const isDeviceSelected = (deviceId) => {
    return selectedDevices.includes(deviceId);
  };

  // Get device name by ID
  const getDeviceName = (deviceId) => {
    const device = availableDevices.find(d => d.id === deviceId);
    return device ? device.name : `Device ${deviceId}`;
  };

  return {
    // State
    selectedDevices,
    availableDevices,
    selectedDeviceObjects,
    loading,
    error,
    
    // Actions
    updateSelectedDevices,
    addDevice,
    removeDevice,
    toggleDevice,
    selectAllDevices,
    deselectAllDevices,
    saveDevices,
    
    // Utilities
    isDeviceSelected,
    getDeviceName,
    setError,
  };
};

export default useDeviceSelection;
